// ------------------------------------------------------------------------------
// name: useUserPermission
// author: mudas
// created: 2025/6/27
// ------------------------------------------------------------------------------

import { useCallback } from 'react';
import { useComputed } from 'common/hooks';
import { usePermission } from 'common/atoms/permission';
import { useUserProjects } from 'common/atoms/userProjects';
import { useAppSelector } from '@tiangong/hooks';
import { getRolePermission } from '@tiangong/utils';

import { AccountType, TaskPermissionType, UserTaskRole } from 'common/types';
import { usePlatformUserConfig } from 'common/atoms/platformUserConfig';

export interface OptionsType {
  allocator?: AccountType;
}

export const useUserPermission = (options?: OptionsType) => {
  const { allocator } = options || {};
  const { platformPermission, currentProjectPermission } = usePermission();
  const { platformUserConfig } = usePlatformUserConfig();
  const { currentPublishProject } = useUserProjects();
  const userAccount = useAppSelector((state) => state.user.userInfo);

  const projectCode = currentPublishProject?.projectCode;

  // 平台+项目角色权限
  const permission = useComputed(
    () =>
      getRolePermission({
        roles: allocator?.uniqueId === userAccount?.uniqueId ? [UserTaskRole.Allocator] : [],
        platformPermission,
        projectPermission: currentProjectPermission,
      }),
    [allocator, userAccount, platformPermission, currentProjectPermission],
  );

  /**
   * 立即检测当前用户是否有指定权限
   * - 主要用于规避在设置完 currentPublishProject 后，无法立即检测权限的情况
   * @param type 权限类型
   * @param code 目标项目code（若不指定，则使用当前环境指定的项目）
   * @param includeProjectRolePermission 是否同时检测当前用户所在项目角色权限
   */
  const hasPermission = useCallback(
    (type: TaskPermissionType, code?: string, includeProjectRolePermission: boolean = true) => {
      return (includeProjectRolePermission ? permission : platformPermission).includes(
        `${code || projectCode}.${type}`,
      );
    },
    [permission, platformPermission, projectCode],
  );

  // 只有平台管理员和项目管理员有指派需求方权限
  const allowAssignDemander = useComputed(
    () => platformPermission.includes(`${projectCode}.${TaskPermissionType.AssignDemander}`),
    [platformPermission, projectCode],
  );

  // 只有平台管理员和项目管理员有指派审核方权限
  const allowAssignReviewer = useComputed(
    () => platformPermission.includes(`${projectCode}.${TaskPermissionType.AssignReviewer}`),
    [platformPermission, projectCode],
  );

  // 是否允许指派制作方
  const allowAssignProducer = useComputed(
    () => permission.includes(`yl_tiangong.${TaskPermissionType.AssignProducer}`),
    [permission],
  );

  // 是否允许邀请创作者
  const allowInviteCreator = useComputed(
    () => permission.includes(`${projectCode}.${TaskPermissionType.InviteCreator}`),
    [permission, projectCode],
  );

  // 是否在发布任务时开放选择“是否申请有灵推荐”
  const allowRecommendApply = useComputed(() => platformUserConfig.recommendApply, [platformUserConfig]);

  return {
    userAccount,
    projectCode,
    hasPermission,
    allowAssignReviewer,
    allowAssignProducer,
    allowAssignDemander,
    allowInviteCreator,
    allowRecommendApply,
  };
};
